import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';

/**
 * Condensed, read-only summary of a submitted Deliverable — mirrors the
 * TarReadOnlyView pattern so both object types feel like a matched pair
 * when opened via the approvals expanded ViewPanel. Uses the raw
 * `HandlersDeliverableResponse` fetched via useGetApiDeliverablesByIdQuery.
 */
export default function DeliverableReadOnlyView({ deliverableData }) {
  if (!deliverableData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const fmtDate = (v) => (v ? dayjs(v).utc().format('DD MMM YYYY') : '-');
  const fmtText = (v) => (v == null || v === '' ? '-' : String(v));

  const d = deliverableData;

  return (
    // Backdrop matches TarReadOnlyView so the two expanded views feel
    // like a coherent set.
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        borderRadius: 1,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack spacing={2}>
        <Section title="Overview">
          <Grid>
            <Field label="Title" value={fmtText(d.deliverableTitle)} />
            <Field
              label="Deliverable Type"
              value={fmtText(
                d.deliverableType?.title || d.deliverableType?.abbreviation,
              )}
            />
            <Field label="Author" value={fmtText(d.author?.name)} />
            <Field
              label="Project"
              value={fmtText(d.project?.title || d.project?.projectCode)}
            />
            <Field label="Date Submitted" value={fmtDate(d.dateSubmitted)} />
            <Field label="Due Date" value={fmtDate(d.dueDate)} />
          </Grid>
        </Section>

        <Section title="Description">
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {fmtText(d.description)}
          </Typography>
        </Section>
      </Stack>
    </Box>
  );
}

// Section: outlined block with a bold heading. Same visual treatment as
// TarReadOnlyView so both expanded views share a look.
function Section({ title, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      {children}
    </Paper>
  );
}

// Grid: responsive 3-up label/value layout, collapsing to 2 then 1.
function Grid({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: '1fr 1fr',
          md: '1fr 1fr 1fr',
        },
        rowGap: 1.25,
        columnGap: 3,
      }}
    >
      {children}
    </Box>
  );
}

// Field: caption label above bold value — matches TarReadOnlyView.
function Field({ label, value }) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
}
